import { useMemo, useEffect } from "react"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import {
  useCubeQuery as _useCubeQuery,
  type UseCubeQueryOptions,
} from "@cubejs-client/react"
import { useAuthControllerCurrentUserQuery } from "~/ui-rtk/api/authApi"
import { useCapabilitiesCache } from "~/ui-rtk/contexts/CapabilitiesContext"
import { MetricsMapping, TMetric } from "../constants/metrics-mapping"
import { type TCubeFilterOptions } from "../shared/types/charts"
import {
  addSorting,
  getPivotData,
  polyfillCubeQueryWithTimeDimension,
} from "../utils/cube"

import useDate from "~/ui-rtk/hooks/date"
import useCurrentCompany from "./current-company"
const dayjs = useDate()

type TWidget = keyof typeof MetricsMapping
type TSummaryCacheEntry = {
  data: unknown[]
  avg?: Record<string, Record<string, unknown>>
}
const IN_MEMORY_SUMMARY_CACHE: Record<string, TSummaryCacheEntry> = {}
const IN_MEMORY_QUERY_CACHE: Record<string, TCubeQuery> = {}

const useCapabilities = () => {
  const { data: user } = useAuthControllerCurrentUserQuery()
  const key = `${user?.currentCompany.id}.${user?.user.dataSource}.capabilities`

  const { data, setCache, allowToFulfill } = useCapabilitiesCache(key)

  const { resultSet } = _useCubeQuery(
    {
      measures: [
        `db_capabilities.name`,
        `db_capabilities.cube`,
        `db_capabilities.table_exists`,
      ],
    },
    {
      skip: !allowToFulfill,
    },
  )

  useEffect(() => {
    if (!resultSet) return
    setCache(
      new Set(
        (resultSet?.rawData() || [])
          .filter(data => data["db_capabilities.table_exists"] === true)
          .map(data => data["db_capabilities.cube"] as string),
      ),
    )
  }, [resultSet, setCache])

  return {
    capabilities: data ?? new Set<string>(),
    isLoaded: data != null,
  }
}

/**
 * Whether a cube's backing ClickHouse table exists for the current company.
 * `isLoaded` stays false until db_capabilities has been fetched, so callers
 * can tell "not available" apart from "not known yet".
 */
export function useCubeAvailability(cubeName: string) {
  const { capabilities, isLoaded } = useCapabilities()
  return { isLoaded, isAvailable: capabilities.has(cubeName) }
}

// export const useCubeQuery = _useCubeQuery

export function useCubeQuery(...args: Parameters<typeof _useCubeQuery>) {
  const { capabilities } = useCapabilities()

  const [query, options] = args

  const q = Array.isArray(query) ? query : [query]
  const dimensions = q.flatMap(q => q.dimensions ?? [])
  const measures = q.flatMap(q => q.measures ?? [])

  const cubeNames = Array.from(
    new Set([...dimensions, ...measures].map(e => e?.split(".")[0])),
  )

  const shouldSkipByCapabilities = cubeNames.every(
    cube => !capabilities.has(cube),
  )
  // Benchmark cubes are not in db_capabilities; bypass capabilities check so
  // benchmark queries run when the caller does not skip (e.g. toggle on).
  const isOnlyBenchmarkCubes =
    cubeNames.length > 0 &&
    cubeNames.every(name => String(name).includes("funnel_benchmarks"))
  const finalSkip = isOnlyBenchmarkCubes
    ? Boolean(options?.skip)
    : shouldSkipByCapabilities || options?.skip

  return _useCubeQuery(query, {
    ...(options || {}),
    skip: finalSkip,
  })
}

const generateKey = (
  companyId: string,
  widget: string,
  startDate: string,
  endDate: string,
  cubeFilters?: TCubeFilterOptions,
  cubeQueryParams?: Record<string, string>,
) =>
  `${companyId}.${widget}.${startDate}.${endDate}.f${JSON.stringify(cubeFilters)}.p${JSON.stringify(cubeQueryParams)}`

export function cubeQuery(customQuery: TCubeQuery, skip = false) {
  const queryOptions: UseCubeQueryOptions = useMemo(
    () => ({
      skip,
    }),
    [skip],
  )

  const {
    resultSet: result,
    isLoading,
    refetch,
  } = useCubeQuery(customQuery, queryOptions)

  return {
    isLoading,
    result,
    refetch,
  }
}

type TAvg = Record<string, Record<string, unknown>>

function prepareAvg(data: Record<string, unknown>, metrics: string[]) {
  return metrics.reduce((acc: TAvg, key: string) => {
    if (!acc[key]) {
      acc[key] = {} as Record<string, unknown>
      acc[key].total = data[key]
      acc[key].count = data[`${key}_count`]
    }
    return acc
  }, {} as TAvg)
}

export function useSummary(
  widget: TWidget,
  dateRange: [Date, Date],
  cubeFilters?: TCubeFilterOptions,
  cubeQueryParams?: Record<string, string>,
  calculateAvg = false,
) {
  const currentCompany = useCurrentCompany()
  if (!currentCompany) {
    throw new Error("No current company")
  }

  const { id: companyId } = currentCompany

  const startDate = dayjs(dateRange[0]).format("YYYY-MM-DD")
  const endDate = dayjs(dateRange[1]).format("YYYY-MM-DD")
  const key = generateKey(
    companyId,
    widget,
    startDate,
    endDate,
    cubeFilters,
    cubeQueryParams,
  )

  const polyfillTimeDimensionPayload = {
    dateRange,
  }

  let query: TCubeQuery
  let avg: Record<string, Record<string, unknown>> | undefined
  if (!IN_MEMORY_QUERY_CACHE[key]) {
    const { q } = MetricsMapping[widget]

    const cubeQ = typeof q === "function" ? q(cubeQueryParams ?? {}) : q

    query = polyfillCubeQueryWithTimeDimension(
      cubeQ as TMetric,
      polyfillTimeDimensionPayload,
    )

    query.limit = 1

    if (cubeFilters) {
      if (typeof cubeFilters === "object" && !Array.isArray(cubeFilters)) {
        const existingFilters = query.filters ?? []
        query.filters = [...existingFilters]

        if (cubeFilters?.and) {
          query.filters.push({ and: cubeFilters.and })
        }

        if (cubeFilters?.extraFilters) {
          query.filters.push(...cubeFilters.extraFilters)
        }
      }
    }

    IN_MEMORY_QUERY_CACHE[key] = query
  } else {
    query = IN_MEMORY_QUERY_CACHE[key]
  }

  const { result, isLoading } = cubeQuery(query, !!IN_MEMORY_SUMMARY_CACHE[key])

  if (result) {
    const res = getPivotData(result, query)
    const { postProcess } = MetricsMapping[widget]
    // Summary widgets always use sync postProcess (no tag/asset enrichment).
    // Cast away the union with Promise so the index access type-checks.
    const processed = (
      postProcess ? postProcess(res, cubeQueryParams ?? {}) : [res]
    ) as [Array<Record<string, unknown>>, string[]?]

    const processedResult = processed[0]

    if (calculateAvg) {
      avg = prepareAvg(processedResult[0], processed[1] as string[])
    }

    IN_MEMORY_SUMMARY_CACHE[key] = {
      data: processedResult,
      avg,
    }
  }

  return {
    isLoading: Boolean(isLoading),
    avg: IN_MEMORY_SUMMARY_CACHE[key] ? IN_MEMORY_SUMMARY_CACHE[key].avg : null,
    summary: IN_MEMORY_SUMMARY_CACHE[key]
      ? IN_MEMORY_SUMMARY_CACHE[key].data[0]
      : null,
  }
}

export function useSummaries({
  widget,
  cubeFilters,
  dateRange,
  compareRange,
  cubeQueryParams,
  calculateAvg = false,
}: {
  widget: TWidget
  dateRange: [Date, Date]
  compareRange?: [Date, Date]
  cubeFilters?: TCubeFilterOptions
  cubeQueryParams?: Record<string, string>
  calculateAvg?: boolean
}) {
  const {
    summary: summaryData,
    avg,
    isLoading,
  } = widget && dateRange
    ? useSummary(widget, dateRange, cubeFilters, cubeQueryParams, calculateAvg)
    : { summary: null, isLoading: false, avg: null }

  const { summary: compareSummaryData, isLoading: isLoadingCompare } =
    compareRange && widget
      ? useSummary(widget, compareRange, cubeFilters, cubeQueryParams)
      : { summary: null, isLoading: false }

  return {
    summaryData,
    avgData: avg,
    compareSummaryData,
    isLoading: isLoading || isLoadingCompare,
  }
}

const defaultMetric = "company_last_updated_date.general_max_date"

export type TLastUpdateDateProps = {
  dateMetric: string
  filterMetrics?: string[]
}

export function useLastUpdateDate(
  { dateMetric, filterMetrics }: TLastUpdateDateProps = {
    dateMetric: defaultMetric,
  },
) {
  const query = useMemo(
    () =>
      addSorting(
        {
          limit: 2,
          dimensions: [dateMetric],
          filters: filterMetrics
            ? [
                {
                  or: filterMetrics.map((filter: string) => ({
                    member: filter,
                    operator: "set",
                  })),
                },
              ]
            : undefined,
        },
        [
          {
            id: dateMetric,
            desc: true,
          },
        ],
      ),
    [dateMetric, filterMetrics],
  )

  const { result, isLoading, refetch } = cubeQuery(query)

  let lastDate: string | undefined

  if (result) {
    const parsedDate = getPivotData(result, query)
    const rawDate =
      parsedDate?.[1]?.[dateMetric] ?? parsedDate?.[0]?.[dateMetric]
    lastDate = rawDate
      ? dayjs(rawDate as string).format("MMM D, YYYY")
      : undefined
  }

  return {
    isLoading,
    lastDate,
    refetch,
  }
}
